import { ErrorModal } from '@app/components/ErrorModal';
import { Price } from '@app/components/Price';
import type {
  Gender,
  MeasurementUnit,
  SizeGuideData,
} from '@app/components/SizeGuide/types';
import {
  useAddCartLineItem,
  useCartAndDeliveryDatesDataContext,
} from '@app/data/clients/cartCheckoutClient';
import { FrontendControl } from '@app/data-stores/ptr/ptr-schemas';
import type {
  ProductTypeValue,
  PTRDataStoreType,
} from '@app/data-stores/ptr/types';
import { AddToCartButton } from '@app/pages/i/components/AddToCartButton';
import { DiscountPromo } from '@app/pages/i/components/DiscountPromo';
import { LoadingBoundary } from '@app/pages/i/components/LoadingBoundary';
import {
  CONFIGURATOR_FORM_ID,
  ProductConfigurator,
} from '@app/pages/i/components/ProductConfigurator';
import {
  AttributeName,
  DSFormOnChangeCallback,
} from '@app/pages/i/components/ProductConfigurator/types';
import { ProductFeaturesReviews } from '@app/pages/i/components/ProductFeaturesReviews';
import {
  getProductSwitchCategoryName,
  ProductStyleSwitcher,
} from '@app/pages/i/components/ProductStyleSwitcher';
import { ProductTrustSignals } from '@app/pages/i/components/ProductTrustSignals/ProductTrustSignals';
import { DeepReadonly } from '@app/types';
import { getImageUrl } from '@app/utils/getImageUrl';
import {
  Alert,
  Box,
  constants,
  Skeleton,
  Stack,
} from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useMemo, useState } from 'react';
import type {
  CategorySet,
  DefaultInventoryItem,
  InventoryItem,
  Price as PriceType,
} from 'types/shop-bff/types';
import type { CurrentConfiguratorValues } from '../../hooks/usePTRProductConfigurator';
import { ListingInfo } from '../ListingInfo';
import styles from '../PrimaryContentSection/PrimaryContentSection.module.scss';
import { ConfiguratorOption } from '../ProductConfigurator/utils';
import { VolumeDiscount } from '../VolumeDiscount';
import { messages } from './messages';
import {
  AddLineItemError,
  getHighestPriorityAddItemErrorType,
  SizeGuideButtonExperience,
} from './utils';

const PriceLoadingComponent = ({ percent }: PriceType['discount']) => (
  <Stack direction={constants.VERTICAL} alignItems="start" spacing="xxs">
    <Box style={{ marginTop: '6px' }}>
      <Skeleton height={24} width={150} animated />
    </Box>
    {percent > 0 && <Skeleton height={24} width={120} animated />}
  </Stack>
);

const MobilePriceLoadingComponent = ({ percent }: PriceType['discount']) => (
  <Stack direction={constants.VERTICAL} alignItems="start" spacing="xxs">
    <Skeleton height={30} width={120} animated />
    {percent > 0 && <Skeleton height={30} width={150} animated />}
  </Stack>
);

export interface ProductDetailsSectionProps {
  configuratorOptions: ConfiguratorOption[];
  currentInventoryItem: InventoryItem;
  inventoryCategorySet: CategorySet;
  availableProducts?: DefaultInventoryItem[] | null;
  sizeGuideData?: SizeGuideData;
  initialGender?: Gender;
  initialMeasurementUnit?: MeasurementUnit;
  onAddToCart: () => void;
  productType: DeepReadonly<ProductTypeValue>;
  productFamily: PTRDataStoreType['productFamilies'][number];
  currentValues: CurrentConfiguratorValues;
  updateForm: DSFormOnChangeCallback;
  showColorConfigSeparately: boolean;
  showingFallback: boolean;
  locale: string;
}

// see sibling implementation in utils.tsx
const getSizeGuideButtonExperience = (
  productType: DeepReadonly<ProductTypeValue>
): SizeGuideButtonExperience | null => {
  if (!productType) return null;

  const hasSizeOption = productType.options.some(
    (opt) =>
      opt.key === AttributeName.Size || opt.key === AttributeName.CardSize
  );

  return hasSizeOption
    ? SizeGuideButtonExperience.Experience1
    : SizeGuideButtonExperience.Experience2;
};

export const ProductDetailsSection: React.FC<ProductDetailsSectionProps> = ({
  configuratorOptions,
  currentInventoryItem,
  inventoryCategorySet,
  availableProducts,
  onAddToCart,
  productType,
  productFamily,
  currentValues,
  updateForm,
  showColorConfigSeparately,
  showingFallback,
  locale,
  sizeGuideData,
  initialGender,
  initialMeasurementUnit,
}) => {
  const intl = useIntl();
  const [{ userId }, setUserInfo] = useUserInfo<UserInfo>();
  const { defaultItem, categoryName } = inventoryCategorySet;
  const {
    price,
    volumeDiscount,
    productTypeId,
    gaCode,
    previewSet,
    stockStatus,
  } = currentInventoryItem;
  const { work } = defaultItem;
  const productTitle = `${work.title} ${categoryName}`;

  const [isSubmitting, setSubmitting] = useState(false);
  const { dispatch, client } = useCartAndDeliveryDatesDataContext();
  const { addCartItem } = useAddCartLineItem({
    client,
    dispatch,
  });

  const [errorModalState, setErrorModalState] = useState<{
    open: boolean;
    type: 'retryable';
    onRetry: () => void;
  }>({
    open: false,
    type: 'retryable',
    onRetry: () => {},
  });

  const artworkUrl = previewSet?.previews.find(
    (p) => p.previewTypeId === 'artwork'
  )?.url;

  const handleAddToCartClick = async () => {
    setSubmitting(true);

    const { errors, cart } = await addCartItem({
      inventoryItemId: currentInventoryItem.id,
      quantity: 1,
    });

    if (!userId && cart) {
      setUserInfo((currentUserInfo) => ({
        ...currentUserInfo,
        userId: cart.user.id,
      }));
    }

    if (errors?.length) {
      const errorType = getHighestPriorityAddItemErrorType(errors);

      if (errorType === AddLineItemError.Retryable) {
        setErrorModalState({
          open: true,
          type: 'retryable',
          onRetry: handleAddToCartClick,
        });
      }

      setSubmitting(false);
      return;
    }

    if (!errors?.length) {
      setSubmitting(false);
      onAddToCart();
      setErrorModalState({
        open: false,
        type: 'retryable',
        onRetry: () => {},
      });
    }
  };

  const isCategoryExistsInProductStyleSwitchCategories =
    !!getProductSwitchCategoryName(productTypeId);

  const sizeGuideButtonExperience = getSizeGuideButtonExperience(productType);

  const hasConfiguratorOptions =
    productType?.options?.length > 0 || productFamily?.options?.length > 0;

  // if there are no available volume discount thresholds
  // we want to hide the mobile price section to ensure an empty
  // box does not appear adding unnecessary space
  const sortedThresholds =
    (volumeDiscount &&
      price &&
      volumeDiscount.thresholds
        .slice()
        .sort((a, b) => a.quantity - b.quantity)
        .filter((discount) => discount.percentOff > price.discount.percent)) ||
    [];

  const filteredConfiguratorOptions = useMemo(() => {
    return configuratorOptions.filter(
      (option) =>
        !showColorConfigSeparately ||
        option.frontendControl !== FrontendControl.Color
    );
  }, [configuratorOptions, showColorConfigSeparately]);

  return (
    <>
      <Box className={styles.listingInfo}>
        <ListingInfo
          name={work.artistShopDisplayName ?? undefined}
          avatar={work.artist?.avatar}
          profileUrl={work.artist?.profileUrl}
          productTitle={productTitle}
        />
      </Box>
      {price && (
        <Box
          className={[styles.price, styles.desktopOnly]
            .filter(Boolean)
            .join(' ')}
          data-testid="product-details-price"
        >
          <LoadingBoundary
            loadingComponent={
              <PriceLoadingComponent
                percent={price.discount.percent}
                amount={price.discount.amount}
              />
            }
          >
            <Price
              amount={price.amount}
              currency={price.currency}
              discount={price.discount}
            />
          </LoadingBoundary>
        </Box>
      )}
      {volumeDiscount && price && sortedThresholds.length > 0 && (
        <Box className={styles.volumeDiscount}>
          <LoadingBoundary
            loadingComponent={<Skeleton height={26} width={200} animated />}
          >
            <div className={styles.desktopOnly}>
              <VolumeDiscount
                originalAmount={price.amount}
                currency={price.currency}
                thresholds={volumeDiscount.thresholds}
                salePercent={price.discount.percent}
              />
            </div>
            <div className={styles.mobileOnly}>
              <Alert
                intent={constants.INFORMATION}
                label={false}
                className={styles.mobileOnly}
              >
                <VolumeDiscount
                  originalAmount={price.amount}
                  currency={price.currency}
                  thresholds={volumeDiscount.thresholds}
                  salePercent={price.discount.percent}
                />
              </Alert>
            </div>
          </LoadingBoundary>
        </Box>
      )}
      {price?.discount && price.discount.percent > 0 && (
        <Box className={styles.discountPromo}>
          <LoadingBoundary
            loadingComponent={<Skeleton height={26} width={180} animated />}
          >
            <DiscountPromo discount={price?.discount.percent} />
          </LoadingBoundary>
        </Box>
      )}
      {Array.isArray(availableProducts) &&
        availableProducts.length > 0 &&
        isCategoryExistsInProductStyleSwitchCategories && (
          <Box className={styles.productStyleSwitcher}>
            <ProductStyleSwitcher
              currentProductTypeId={productTypeId}
              availableProducts={availableProducts}
              artistName={work.artist?.name || ''}
              matureContent={work.matureContent}
            />
          </Box>
        )}

      <Box
        className={`${styles.productConfigurator} ${
          hasConfiguratorOptions ? '' : styles.hidden
        }`}
      >
        {showingFallback && productFamily?.warnOnOptionFallback && (
          <Box marginBottom="m">
            <Alert intent={constants.WARNING} className={styles.fallbackAlert}>
              {intl.formatMessage(messages.fallbackAlert)}
            </Alert>
          </Box>
        )}

        {productType && (
          <ProductConfigurator
            configuratorOptions={filteredConfiguratorOptions}
            productType={productType}
            productFamily={productFamily}
            currentInventoryItem={currentInventoryItem}
            currentValues={currentValues}
            onSubmit={handleAddToCartClick}
            updateForm={updateForm}
            locale={locale}
            showSizeGuideButton={
              sizeGuideButtonExperience ===
                SizeGuideButtonExperience.Experience1 && sizeGuideData != null
            }
            sizeGuideData={sizeGuideData}
            initialGender={initialGender}
            initialMeasurementUnit={initialMeasurementUnit}
          />
        )}
      </Box>
      <Box className={styles.addToCart} data-testid="add-to-cart-section">
        {price && (
          <Box className={styles.mobileOnly}>
            <LoadingBoundary
              loadingComponent={
                <MobilePriceLoadingComponent
                  percent={price.discount.percent}
                  amount={price.discount.amount}
                />
              }
            >
              <Price
                amount={price.amount}
                currency={price.currency}
                discount={price.discount}
                size="mediumSmall"
              />
            </LoadingBoundary>
          </Box>
        )}
        <Box className={`${styles.desktopOnly} ${styles.ATCButton}`}>
          <LoadingBoundary loadingComponent={<Skeleton height={60} animated />}>
            <AddToCartButton
              loading={isSubmitting}
              formId={CONFIGURATOR_FORM_ID}
              size="large"
              fontSize="display3"
              outOfStock={stockStatus.status === 'out-of-stock'}
            />
          </LoadingBoundary>
        </Box>
        <Box
          className={[styles.mobileOnly, styles.ATCButton]
            .filter(Boolean)
            .join(' ')}
          data-testid="mobile-add-to-cart"
        >
          <LoadingBoundary
            loadingComponent={<Skeleton height={56} width={185} animated />}
          >
            <AddToCartButton
              loading={isSubmitting}
              formId={CONFIGURATOR_FORM_ID}
              size="large"
              fontSize="display3"
              outOfStock={stockStatus.status === 'out-of-stock'}
            />
          </LoadingBoundary>
        </Box>
        <ErrorModal
          type={errorModalState.type}
          open={errorModalState.open}
          imageSrc={artworkUrl || getImageUrl('/icons/empty-cart.svg')}
          onRetry={errorModalState.onRetry}
          onClose={() =>
            setErrorModalState((prev) => ({ ...prev, open: false }))
          }
        />
      </Box>
      <ErrorBoundary>
        <Box className={styles.productTrustSignals}>
          <ProductTrustSignals
            workId={currentInventoryItem.workId}
            productTypeId={currentInventoryItem.productTypeId}
            showSizeGuideButton={
              sizeGuideButtonExperience ===
                SizeGuideButtonExperience.Experience2 && sizeGuideData != null
            }
            analyticsCode={gaCode}
            sizeGuideData={sizeGuideData}
            initialGender={initialGender}
            initialMeasurementUnit={initialMeasurementUnit}
          />
        </Box>
        <Box className={styles.productFeaturesReviews}>
          <ProductFeaturesReviews
            inventoryItemId={currentInventoryItem.id}
            productTypeId={currentInventoryItem.productTypeId}
          />
        </Box>
      </ErrorBoundary>
    </>
  );
};
